import * as sentry from "@sentry/react";

import { CUSTOMERIO_BASE_PROPS } from "./data";
import { formatCustomerIOEventProps } from "./formatEventProps";
import { getCustomerIOClient, getCustomerIOEventContext } from "./init";
import { TCustomerIOTraits, TCustomerIOUser } from "./types";

// cdp.customer.io is commonly adblocked, so only report the first failure
let _reportedFailure = false;

const reportFailure = (label: string, e: unknown) => {
  if (_reportedFailure) {
    sentry.addBreadcrumb({
      category: "customerio",
      level: "warning",
      message: `${label} failed`,
    });
    return;
  }

  _reportedFailure = true;
  console.warn(
    `Customer.io request failed (${label}) — further failures will only be logged as breadcrumbs`,
    e,
  );
  sentry.captureException(e);
};

const safeCall = (
  label: string,
  call: (
    client: NonNullable<ReturnType<typeof getCustomerIOClient>>,
  ) => unknown,
) => {
  const client = getCustomerIOClient();
  if (!client) return;

  try {
    Promise.resolve(call(client)).catch((e) => reportFailure(label, e));
  } catch (e) {
    reportFailure(label, e);
  }
};

export const trackCustomerIOEvent = (
  event: string,
  properties: Record<string, unknown> = {},
) =>
  safeCall(`track: ${event}`, (client) =>
    client.track(event, {
      ...getCustomerIOEventContext(),
      ...formatCustomerIOEventProps(properties),
      // Last, so a caller cannot mislabel a website event
      ...CUSTOMERIO_BASE_PROPS,
      location: window.location.pathname,
    }),
  );

export const identifyCustomerIOUser = (
  user: TCustomerIOUser,
  traits: TCustomerIOTraits = {},
) =>
  safeCall("identify", (client) =>
    client.identify(user.email, {
      email: user.email,
      uid: user.uid,
      ...traits,
    }),
  );

export const trackCustomerIOPage = (
  path: string,
  properties: TCustomerIOTraits = {},
) =>
  safeCall("page", (client) =>
    client.page(path, { ...properties, ...CUSTOMERIO_BASE_PROPS }),
  );

export const resetCustomerIOUser = () =>
  safeCall("reset", (client) => client.reset());
